import React, { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useFeatures } from 'flagged'
import { Box, Button, Card, CardContent, TextField } from '@mui/material'
import { useTheme } from '../../../../components/mui/Styles/index'
import FormHeading from '../../../../components/userauthentication/FormHeading'
import { forgotPasswordConfirm } from '../../../../api/userauthentication/forgotpassword'
import { GenericSnackbar } from '../../../../components/GenericSnackbar/generic-snackbar'
import { getAmplifyPasswordExceptionMessage } from '../../../../utils/aws-cognito-utils'
import { businessMessages } from '../../../../constants/business-message-constants'
import * as Constants from '../../../../constants/en.json'
import { confirmForgotPassword } from '../../../../api/user/forgot-password'
import { setSnackbar } from '../../../../components/GenericSnackbar/generic-snackbar-slice'
import PasswordValidation from '../../../../components/PasswordValidation/password-validation'

/* Confirm Reset Password Form */
function SetNewPassword() {
  const theme = useTheme()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const features = useFeatures()
  const isUserServiceEnabled = features.isUserServiceEnabled
  const [formData, setFormData] = useState({})
  const [newPassword, setNewPassword] = useState('')
  const [newPasswordAgain, setNewPasswordAgain] = useState('')
  const [emailAddress, setEmailAddress] = useState('')
  const [confirmationCode, setConfirmationCode] = useState('')
  const [isPasswordValid, setIsPasswordValid] = useState(false)
  const isFormValid =
    !emailAddress ||
    !confirmationCode ||
    !newPassword ||
    !newPasswordAgain ||
    !isPasswordValid ||
    newPassword !== newPasswordAgain

  useEffect(() => {
    setFormData({
      emailAddress,
      confirmationCode,
      newPassword,
      newPasswordAgain,
    })
  }, [newPassword, newPasswordAgain, confirmationCode, emailAddress])

  const isNewPasswordValid = () => {
    return formData.newPassword === formData.newPasswordAgain
  }

  const handlePasswordValidation = (isValid) => {
    setIsPasswordValid(isValid)
  }

  const handleSubmitNewPassword = () => {
    if (isNewPasswordValid()) {
      if (!isUserServiceEnabled) {
        forgotPasswordConfirm(formData)
          .then(() => {
            navigate('/set-new-password-success')
          })
          .catch((exception) => {
            const message = exception?.message
              ? getAmplifyPasswordExceptionMessage(exception)
              : businessMessages.password.UNEXPECTED_EXCEPTION
            dispatch(setSnackbar({ message, severity: 'error', action: true }))
          })
      } else {
        const request = {
          email: formData.emailAddress,
          confirmationCode: formData.confirmationCode,
          password: formData.newPassword,
        }
        confirmForgotPassword(request)
          .then(() => {
            navigate('/set-new-password-success')
          })
          .catch((exception) => {
            const message = exception?.message
              ? getAmplifyPasswordExceptionMessage(exception)
              : businessMessages.password.UNEXPECTED_EXCEPTION
            dispatch(setSnackbar({ message, severity: 'error', action: true }))
          })
      }
    }
    if (!isNewPasswordValid()) {
      const message = businessMessages.password.PASSWORD_MISMATCH
      dispatch(setSnackbar({ message, severity: 'error', action: true }))
    }
  }

  const setNewPasswordBoxStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  }
  const passwordValidationBoxStyle = { width: 240, my: 3 }
  const submitButtonStyle = {
    mt: 2,
    ml: 1,
    backgroundColor: theme.palette.secondary.main,
    '&:hover': {
      backgroundColor: theme.palette.secondary.dark,
    },
  }
  const textFieldStyle = { width: '240px' }

  return (
    <Box sx={theme.soloContainer}>
      <Card className='soloCard'>
        <CardContent>
          <Box sx={setNewPasswordBoxStyle}>
            <FormHeading formHeader='Set New Password' />
            <TextField
              onChange={(e) => setEmailAddress(e.target.value)}
              required
              label={Constants.setNewPassword.EMAIL}
              variant='outlined'
              type='text'
              placeholder={Constants.setNewPassword.EMAIL}
              sx={textFieldStyle}
            />
            <TextField
              onChange={(e) => setConfirmationCode(e.target.value)}
              required
              label={Constants.setNewPassword.TEMPORARY_PASSWORD}
              variant='outlined'
              type='text'
              placeholder={Constants.setNewPassword.TEMPORARY_PASSWORD}
              sx={textFieldStyle}
            />
            <TextField
              onChange={(e) => setNewPassword(e.target.value)}
              required
              label={Constants.setNewPassword.NEW_PASSWORD}
              variant='outlined'
              type='password'
              placeholder={Constants.setNewPassword.NEW_PASSWORD}
              sx={textFieldStyle}
            />
            <TextField
              onChange={(e) => setNewPasswordAgain(e.target.value)}
              required
              label={Constants.setNewPassword.CONFIRM_NEW_PASSWORD}
              variant='outlined'
              type='password'
              placeholder={Constants.setNewPassword.CONFIRM_NEW_PASSWORD}
              sx={textFieldStyle}
            />
            <Box sx={passwordValidationBoxStyle}>
              <PasswordValidation
                value={newPassword}
                handlePasswordValidation={handlePasswordValidation}
              />
            </Box>
            <Button
              onClick={handleSubmitNewPassword}
              variant='contained'
              disabled={isFormValid}
              sx={submitButtonStyle}
            >
              {Constants.setNewPassword.SUBMIT_NEW_PASSWORD}
            </Button>
          </Box>
        </CardContent>
      </Card>
      <GenericSnackbar />
    </Box>
  )
}
export default SetNewPassword
